import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Sparkles,
  Upload,
  BookOpen,
  Users,
  Library,
  Settings,
  ChevronRight,
  X,
  Menu,
  Workflow
} from 'lucide-react';

const navigationItems = [
  { id: "dashboard",        icon: LayoutDashboard, label: "Dashboard",           href: "#dashboard" },
  { id: "workflow",         icon: Workflow,        label: "Workflow Editor",    href: "#workflow" },
  { id: "upload-image",     icon: Upload,          label: "Upload Visuals",     href: "#upload-image" },
  { id: "generate-content", icon: Sparkles,        label: "Generate Content",   href: "#generate-content" },
  { id: "lesson-planner",   icon: BookOpen,        label: "Lesson Planner",     href: "#lesson-planner" },
  { id: "student-tracker",  icon: Users,           label: "Student Tracker",    href: "#student-tracker" },
  { id: "resource-library", icon: Library,         label: "Resource Library",   href: "#resource-library" },
  { id: "settings",         icon: Settings,        label: "Settings",           href: "#settings" },
];

const SahayakSidebar = ({ isOpen, toggleSidebar, onNavigate, currentView }) => {
  const [hoveredItem, setHoveredItem] = useState(null);

  // Auto-close sidebar on window resize (mobile behavior)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && isOpen) {
        toggleSidebar();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, toggleSidebar]);

  // Disable body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 1024) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleNavigation = (item) => {
    if (onNavigate) {
      onNavigate(item);
    }
    // Auto-close on mobile after navigation
    if (window.innerWidth < 1024) {
      toggleSidebar();
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 shadow-lg flex flex-col
          transition-all duration-300 ease-in-out
          ${isOpen ? "w-64" : "w-0 lg:w-16"}`}
      >
        <div className={`flex flex-col h-full ${!isOpen ? "lg:flex" : "flex"}`}>
          {/* Header */}
          <div className={`border-b border-gray-100 bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-300
            ${isOpen ? "px-6 py-5" : "lg:px-3 lg:py-4"}`}>
            <div className="flex items-center justify-between">
              {/* Logo/Title */}
              <div className="flex items-center space-x-3">
                {!isOpen && (
                  <button
                    onClick={toggleSidebar}
                    className="hidden lg:flex w-8 h-8 bg-white/20 rounded-lg items-center justify-center hover:bg-white/30 transition-all duration-200 hover:scale-110"
                    title="Expand sidebar"
                  >
                    <span className="text-white font-bold text-sm">S</span>
                  </button>
                )}
                <h1
                  className={`text-xl font-bold text-white transition-all duration-300
                    ${!isOpen ? "lg:opacity-0 lg:w-0 lg:overflow-hidden" : "opacity-100"}`}
                >
                  Sahayak
                </h1>
              </div>
              
              {/* Control Buttons */}
              <div className="flex items-center">
                {/* Desktop hamburger menu */}
                <button
                  onClick={toggleSidebar}
                  className="hidden lg:flex items-center justify-center w-8 h-8 hover:bg-white/10 rounded-lg transition-all duration-200 group"
                  title={isOpen ? "Collapse sidebar" : "Expand sidebar"}
                >
                  <div className="flex flex-col space-y-1">
                    <div className={`w-4 h-0.5 bg-white transition-all duration-200 group-hover:bg-white/80
                      ${!isOpen ? "rotate-45 translate-y-1.5" : ""}`}></div>
                    <div className={`w-4 h-0.5 bg-white transition-all duration-200 group-hover:bg-white/80
                      ${!isOpen ? "opacity-0" : ""}`}></div>
                    <div className={`w-4 h-0.5 bg-white transition-all duration-200 group-hover:bg-white/80
                      ${!isOpen ? "-rotate-45 -translate-y-1.5" : ""}`}></div>
                  </div>
                </button>
                
                {/* Mobile close button */}
                <button
                  onClick={toggleSidebar}
                  className="lg:hidden p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 overflow-y-auto ${isOpen ? "p-4" : "lg:p-2"} space-y-1`}>
            {navigationItems.map((item, idx) => {
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item)}
                  className={`w-full flex items-center transition-all duration-200 group relative
                    ${isOpen 
                      ? "gap-3 px-4 py-3 rounded-xl text-sm font-medium" 
                      : "lg:justify-center lg:p-3 lg:rounded-lg"}
                    ${isActive
                      ? isOpen 
                        ? "bg-blue-50 text-blue-700 shadow-sm border-l-4 border-blue-600"
                        : "lg:bg-blue-600 lg:text-white"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 lg:hover:bg-gray-100"}`}
                  onMouseEnter={() => setHoveredItem(idx)}
                  onMouseLeave={() => setHoveredItem(null)}
                  title={!isOpen ? item.label : ""}
                >
                  <item.icon
                    className={`w-5 h-5 transition-all flex-shrink-0
                      ${isActive ? (isOpen ? "text-blue-600" : "lg:text-white") : ""}
                      ${hoveredItem === idx ? "scale-110" : ""}`}
                  />
                  <span
                    className={`transition-all duration-300 font-medium overflow-hidden whitespace-nowrap
                      ${!isOpen ? "lg:w-0 lg:opacity-0" : "opacity-100"}
                      ${isActive ? "text-blue-700" : ""}`}
                  >
                    {item.label}
                  </span>
                  {isActive && isOpen && (
                    <ChevronRight className="w-4 h-4 ml-auto text-blue-500" />
                  )}
                  
                  {/* Tooltip for collapsed state */}
                  {!isOpen && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                      {item.label}
                    </div>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Footer */}
          <div className={`border-t border-gray-100 bg-gray-50 ${isOpen ? "p-4" : "lg:p-2"}`}>
            <div className={`text-xs text-gray-500 transition-all duration-300 ${!isOpen ? "lg:opacity-0 lg:h-0 lg:overflow-hidden" : ""}`}>
              <div className="text-center">
                <span className="font-medium">v2.0.1</span>
              </div>
            </div>
            {!isOpen && (
              <div className="hidden lg:flex justify-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toggle buttons */}
      {!isOpen && (
        <>
          {/* Mobile menu button */}
          <button
            onClick={toggleSidebar}
            className="fixed top-4 left-4 z-30 lg:hidden p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          
        </>
      )}
    </>
  );
};

export default SahayakSidebar;